import React from 'react';
import PropTypes from 'prop-types';
import styles from './Toggle.module.scss';

/**
 * A reusable Toggle/Switch component
 * 
 * @example
 * <Toggle
 *   checked={isActive}
 *   onChange={(checked) => setIsActive(checked)}
 *   label="Enable notifications"
 * />
 */
const Toggle = ({
  checked,
  onChange,
  disabled,
  label,
  labelPosition,
  size,
  name,
  id,
  className,
  activeColor,
  inactiveColor,
}) => {
  const toggleId = id || `toggle-${name || Math.random().toString(36).substr(2, 9)}`;

  const handleChange = (e) => {
    if (disabled) return;
    onChange(e.target.checked, e);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(!checked);
    }
  };

  const sizeClass = styles[size] || styles.medium;
  
  const customStyles = {
    '--toggle-active-color': activeColor,
    '--toggle-inactive-color': inactiveColor,
  };

  return (
    <div 
      className={`${styles.toggleWrapper} ${className || ''} ${labelPosition === 'left' ? styles.labelLeft : ''}`}
      style={customStyles}
    >
      {label && labelPosition === 'left' && (
        <label htmlFor={toggleId} className={styles.label}>
          {label}
        </label>
      )}
      
      <label 
        className={`${styles.toggle} ${sizeClass} ${disabled ? styles.disabled : ''}`}
        htmlFor={toggleId}
      >
        <input
          type="checkbox"
          id={toggleId}
          name={name}
          checked={checked}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-checked={checked}
          aria-label={label || 'Toggle switch'}
          role="switch"
          tabIndex={0}
        />
        <span className={styles.slider} />
      </label>

      {label && labelPosition === 'right' && (
        <label htmlFor={toggleId} className={styles.label}>
          {label}
        </label>
      )}
    </div>
  );
};

Toggle.propTypes = {
  /** Whether the toggle is checked/on */
  checked: PropTypes.bool,
  /** Callback function when toggle state changes - receives (checked, event) */
  onChange: PropTypes.func.isRequired,
  /** Whether the toggle is disabled */
  disabled: PropTypes.bool,
  /** Label text for the toggle */
  label: PropTypes.string,
  /** Position of the label relative to toggle */
  labelPosition: PropTypes.oneOf(['left', 'right']),
  /** Size of the toggle */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Name attribute for the input */
  name: PropTypes.string,
  /** ID attribute for the input */
  id: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
  /** Custom active/on color */
  activeColor: PropTypes.string,
  /** Custom inactive/off color */
  inactiveColor: PropTypes.string,
};

Toggle.defaultProps = {
  checked: false,
  disabled: false,
  label: '',
  labelPosition: 'right',
  size: 'medium',
  name: '',
  id: '',
  className: '',
  activeColor: '#39979d', // Primary button color from theme (teal)
  inactiveColor: '#c4c4c4', // Light gray for inactive state
};

export default Toggle;
